import type { PaymentResponse } from '../../api/types';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getUserById } from '../../api/endpoints/admin';
import { usePayment } from './hooks';
import { ReasonDialog } from './ReasonDialog';
import { OrderLabel } from './OrderLabel';
import { formatMoney, parsePaise } from './allocation';
import { StatusChip } from './StatusChip';
import styles from './Payments.module.css';

/** Bound to one freshly read record; cancellation never reverses a bank transfer. */
export function CancelPaymentDialog({
  paymentId,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  paymentId: number;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const query = usePayment(paymentId);
  const refresh = useRef(query.refetch);
  const guard = useRef(false);
  const [verified, setVerified] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    void refresh
      .current()
      .then((result) => {
        if (active) {
          setVerified(!result.isError);
          setFailed(result.isError);
        }
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [paymentId]);
  const payment = query.data;
  const identity = useQuery({
    queryKey: ['payment-counterparty', payment?.counterpartyUserId],
    queryFn: () => getUserById(payment!.counterpartyUserId),
    enabled: Boolean(payment),
    retry: false,
  });
  const profile = identity.data?.profile;
  const name =
    profile && 'companyName' in profile && typeof profile.companyName === 'string'
      ? profile.companyName || identity.data?.user.fullName
      : identity.data?.user.fullName;
  const eligible =
    verified &&
    !query.isFetching &&
    payment?.id === paymentId &&
    (payment.direction === 'IN'
      ? payment.status === 'PAID'
      : ['PAID', 'APPROVED'].includes(payment.status));
  const label = payment?.direction === 'IN' ? 'Cancel receipt' : 'Cancel payout';
  return (
    <ReasonDialog
      title={label}
      actionLabel={label}
      help="cancel-payment"
      cancelLabel={payment?.direction === 'IN' ? 'Keep receipt' : 'Keep payout'}
      onCancel={onClose}
      submitting={submitting}
      error={error}
      disabled={!eligible}
      onSubmit={async (reason) => {
        if (!eligible || submitting || guard.current) return;
        guard.current = true;
        try {
          await onSubmit(reason);
        } finally {
          guard.current = false;
        }
      }}
      context={
        <>
          {!verified && !failed && <p role="status">{t('payments.cancelPaymentDialog.refreshing')}</p>}
          {failed && (
            <p role="alert">
              {t('payments.cancelPaymentDialog.refreshFailed')}{' '}
              <button
                onClick={async () => {
                  setVerified(false);
                  const result = await refresh.current();
                  setFailed(result.isError);
                  setVerified(!result.isError);
                }}
              >
                {t('payments.cancelPaymentDialog.retry')}
              </button>
            </p>
          )}
          {verified && !eligible && (
            <p role="alert">
              {t('payments.cancelPaymentDialog.notCancellable')}
            </p>
          )}
          {payment && <CancelPaymentContext payment={payment} name={name} />}
        </>
      }
    />
  );
}

export function CancelPaymentContext({
  payment,
  name,
}: {
  payment: PaymentResponse;
  name?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.cancellationContext}>
      <p>
        <strong>
          {payment.direction === 'IN' ? 'Receipt' : 'Payout'} #{payment.id} ·{' '}
          {formatMoney(parsePaise(payment.amount))}
        </strong>
      </p>
      <p>{name || `Counterparty #${payment.counterpartyUserId}`}</p>
      <p className={styles.statusTransition} aria-label={t('payments.cancelPaymentContext.statusAfter')}>
        <StatusChip displayStatus={payment.status} />
        <span aria-hidden="true">→</span>
        <StatusChip displayStatus="VOID" />
      </p>
      {!name && (
        <p>
          {t('payments.cancelPaymentContext.nameUnavailable', { userId: payment.counterpartyUserId })}
        </p>
      )}
      <p>
        {payment.method || 'Transfer'} · {payment.reference || 'No reference'}
      </p>
      <h3>{t('payments.cancelPaymentContext.affectedOrders')}</h3>
      {payment.allocations
        .filter((a) => a.status === 'ACTIVE')
        .map((a) => (
          <p className={styles.cancellationOrder} key={a.id}>
            {t('payments.cancelPaymentContext.allocation', { id: a.id })}
            <OrderLabel {...a} newTab />
            {t('payments.cancelPaymentContext.allocationAmounts', {
              cash: formatMoney(parsePaise(a.amount)),
              tds: formatMoney(parsePaise(a.tdsAmount)),
            })}
          </p>
        ))}
      {!payment.allocations.some((a) => a.status === 'ACTIVE') && <p>{t('payments.cancelPaymentContext.noActiveAllocations')}</p>}
      <div className={styles.cancellationEffects}>
        <p>
          <span aria-hidden="true">− </span>
          <strong>{t('payments.cancelPaymentContext.allocationsRemoved')}</strong>
          <br />
          {t('payments.cancelPaymentContext.allocationsRemovedDetail')}
        </p>
        <p>
          <span aria-hidden="true">↩ </span>
          <strong>
            {payment.direction === 'IN'
              ? 'Order outstanding reopens'
              : payment.status === 'PAID'
                ? 'Partner payable reopens'
                : 'Unpaid reservation released'}
          </strong>
          <br />
          {payment.direction === 'IN'
            ? 'No reusable credit remains on the cancelled receipt.'
            : payment.status === 'PAID'
              ? 'The removed settlement becomes payable again.'
              : 'The reserved amount becomes available for a new payout request.'}
        </p>
      </div>
      <p>{t('payments.cancelPaymentContext.noBankReversal')}</p>
      <p>{t('payments.cancelPaymentContext.historyKept')}</p>
    </div>
  );
}
